import { useEffect } from "react";
import { RouterProvider, createRouter, type RouterHistory } from "@tanstack/react-router";
import { useSession } from "@/auth/session";
import { RouteErrorPage } from "@/components/pages";
import { Splash } from "@/components/ui";
import { accountSecurityRoute } from "@/routes/account";
import { analyticsRoute } from "@/routes/analytics";
import { approvalsRoute } from "@/routes/approvals";
import { auditRoute } from "@/routes/audit";
import { batchDetailRoute, batchesIndexRoute, batchesRoute } from "@/routes/batches";
import { certificateDetailRoute, certificatesRoute } from "@/routes/certificates";
import { deliveriesRoute } from "@/routes/deliveries";
import { emailTemplateDetailRoute, emailTemplatesRoute } from "@/routes/emailTemplates";
import { fontsRoute } from "@/routes/fonts";
import { homeRoute } from "@/routes/home";
import { loginRoute } from "@/routes/login";
import { memberDetailRoute, membersRoute } from "@/routes/members";
import { paymentsRoute } from "@/routes/payments";
import { printerDetailRoute, printersRoute } from "@/routes/printers";
import { programDetailRoute, programsRoute } from "@/routes/programs";
import { reissuesRoute } from "@/routes/reissues";
import { revocationsRoute } from "@/routes/revocations";
import { roleDetailRoute, rolesRoute } from "@/routes/roles";
import { rootRoute } from "@/routes/root";
import { signatureDetailRoute, signaturesRoute } from "@/routes/signatures";
import { staffRoute } from "@/routes/staff";
import { templateDesignerRoute, templateDetailRoute, templatesRoute } from "@/routes/templates";
import { userDetailRoute, usersRoute } from "@/routes/users";

const routeTree = rootRoute.addChildren([
  loginRoute,
  staffRoute.addChildren([
    homeRoute,
    accountSecurityRoute,
    batchesRoute.addChildren([batchesIndexRoute, batchDetailRoute]),
    certificatesRoute,
    certificateDetailRoute,
    membersRoute,
    memberDetailRoute,
    paymentsRoute,
    templatesRoute,
    templateDetailRoute,
    templateDesignerRoute,
    signaturesRoute,
    signatureDetailRoute,
    fontsRoute,
    printersRoute,
    printerDetailRoute,
    programsRoute,
    programDetailRoute,
    usersRoute,
    userDetailRoute,
    rolesRoute,
    roleDetailRoute,
    auditRoute,
    analyticsRoute,
    approvalsRoute,
    revocationsRoute,
    reissuesRoute,
    deliveriesRoute,
    emailTemplatesRoute,
    emailTemplateDetailRoute,
  ]),
]);

/** Tests pass a memory history; the app uses the browser's. */
export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    // Real value arrives through <RouterProvider context>; the router never loads before that.
    context: { session: undefined! },
    defaultErrorComponent: RouteErrorPage,
  });
}

export type AppRouterInstance = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouterInstance;
  }
}

export function AppRouter({ router }: { router: AppRouterInstance }) {
  const session = useSession();

  // Guards read the session in beforeLoad, so re-run them whenever sign-in state changes.
  useEffect(() => {
    if (!session.loading) void router.invalidate();
  }, [router, session.loading, session.me]);

  if (session.loading) return <Splash message="Restoring session..." />;
  return <RouterProvider router={router} context={{ session }} />;
}
